/**
 * Telegram Web App Detection Utilities
 * Used to detect if app is running inside Telegram Mini App
 * and access Telegram-provided user data
 */

/**
 * Check if app is running as a Telegram Mini App
 * @returns {boolean} true if inside Telegram with valid initData
 */
export const isTelegramWebApp = () => {
  return !!window.Telegram?.WebApp?.initData;
};

/**
 * Get the raw initData string (URL-encoded)
 * This should be sent to backend for verification
 * @returns {string} initData string or empty string
 */
export const getTelegramInitData = () => {
  return window.Telegram?.WebApp?.initData || "";
};

/**
 * Get parsed user data from Telegram (unsafe - not verified)
 * Use this for UI display only, not for authentication
 * @returns {object|null} user object or null
 */
export const getTelegramUser = () => {
  return window.Telegram?.WebApp?.initDataUnsafe?.user || null;
};

/**
 * Get the start_param from the Mini App link
 * e.g., t.me/bot/app?startapp=job_123 -> "job_123"
 * @returns {string|null} start parameter or null
 */
export const getTelegramStartParam = () => {
  return window.Telegram?.WebApp?.initDataUnsafe?.start_param || null;
};

/**
 * Get Telegram WebApp instance
 * @returns {object|null} Telegram.WebApp object or null
 */
export const getTelegramWebApp = () => {
  return window.Telegram?.WebApp || null;
};

/**
 * Initialize Telegram Web App with best practices
 * Call this when the app loads inside Telegram
 */
export const initTelegramWebApp = () => {
  const tg = window.Telegram?.WebApp;
  if (!tg) return;

  // Signal that the app is ready
  tg.ready();

  // Expand to full screen
  tg.expand();

  // Prevent accidental closing
  if (tg.enableClosingConfirmation) {
    tg.enableClosingConfirmation();
  }

  // Apply Telegram theme colors for seamless look
  if (tg.themeParams) {
    document.body.style.backgroundColor = tg.themeParams.bg_color || '#ffffff';
    document.body.style.color = tg.themeParams.text_color || '#000000';
  }
};

/**
 * Provide haptic feedback
 * @param {string} type - 'success', 'warning', 'error', or 'light', 'medium', 'heavy', 'rigid', 'soft'
 */
export const hapticFeedback = (type) => {
  const tg = window.Telegram?.WebApp;
  if (!tg?.HapticFeedback) return;

  if (['success', 'warning', 'error'].includes(type)) {
    tg.HapticFeedback.notificationOccurred(type);
  } else {
    tg.HapticFeedback.impactOccurred(type);
  }
};

/**
 * Request user's phone number using native Telegram dialog
 * @returns {Promise<string|null>} phone number or null if declined
 */
export const requestTelegramContact = () => {
  return new Promise((resolve) => {
    const tg = window.Telegram?.WebApp;
    if (!tg?.requestContact) {
      resolve(null);
      return;
    }

    tg.requestContact((sent, event) => {
      if (sent && event?.responseUnsafe?.contact?.phone_number) {
        resolve(event.responseUnsafe.contact.phone_number);
      } else {
        resolve(null);
      }
    });
  });
};

/**
 * Check if write access (notification permission) is already granted
 * @returns {boolean} true if already granted
 */
export const hasWriteAccess = () => {
  const user = window.Telegram?.WebApp?.initDataUnsafe?.user;
  return user?.allows_write_to_pm === true;
};

/**
 * Request permission to send messages to user (notifications)
 * NOTE: Popup will NOT show if permission is already granted
 * @returns {Promise<boolean>} true if granted (newly or previously), false if declined
 */
export const requestTelegramWriteAccess = () => {
  return new Promise((resolve) => {
    const tg = window.Telegram?.WebApp;

    // Check if WebApp exists
    if (!tg) {
      console.error("Telegram WebApp not available");
      resolve(false);
      return;
    }

    // Check if method exists
    if (typeof tg.requestWriteAccess !== 'function') {
      console.warn("requestWriteAccess not available");
      resolve(false);
      return;
    }

    console.log("Calling requestWriteAccess NOW...");

    let resolved = false;

    try {
      // Call the method
      tg.requestWriteAccess((granted) => {
        if (!resolved) {
          resolved = true;
          console.log("requestWriteAccess callback:", granted);
          resolve(granted);
        }
      });
    } catch (error) {
      console.error("requestWriteAccess error:", error);
      resolve(false);
      return;
    }

    // Also listen for event
    try {
      const handleEvent = (eventData) => {
        if (!resolved) {
          resolved = true;
          console.log("writeAccessRequested event:", eventData);
          tg.offEvent('writeAccessRequested', handleEvent);
          resolve(eventData?.status === 'allowed');
        }
      };
      tg.onEvent('writeAccessRequested', handleEvent);
    } catch (e) {
      console.error("Event listener error:", e);
    }

    // Timeout fallback
    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        console.warn("requestWriteAccess timeout after 10s");
        resolve(false);
      }
    }, 10000);
  });
};

/**
 * Close the Mini App
 */
export const closeTelegramWebApp = () => {
  window.Telegram?.WebApp?.close();
};

/**
 * Parse the startapp parameter from Mini App link
 * e.g., "4_2386" -> { companyId: "4", jobId: "2386" }
 * e.g., "job_4_2386" -> { type: "job", companyId: "4", jobId: "2386" }
 * @param {string} startParam - The start_param from Telegram
 * @returns {object|null} Parsed parameters or null
 */
export const parseStartParam = (startParam) => {
  if (!startParam) return null;

  const parts = startParam.split('_');

  // Format: companyId_jobId (e.g., "4_2386")
  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
    return {
      companyId: parts[0],
      jobId: parts[1]
    };
  }

  // Format: type_companyId_jobId (e.g., "job_4_2386")
  if (parts.length === 3) {
    return {
      type: parts[0],
      companyId: parts[1],
      jobId: parts[2]
    };
  }

  // Format: region_country_companyId_jobId (e.g., "region_UZ_4_2386")
  if (parts.length === 4 && parts[0] === 'region') {
    return {
      region: parts[1],
      companyId: parts[2],
      jobId: parts[3]
    };
  }

  return { raw: startParam };
};
